import { useFlags } from 'launchdarkly-react-client-sdk';

import { useCore } from '@phoenix/core/frontend/shared/feature/context/core.context';

import {
  NextBestOffer,
  NextBestOfferOrigin,
  useGetCustomerRecommendationsQuery,
} from '@phoenix/team-m/dashboard/frontend/domain/generated/graphql';
import { useQueryContextMemo } from '@phoenix/team-m/dashboard/frontend/shared/use-query-context-memo';
import { mapProductCategory, mapProductDriveType } from '@phoenix/team-m/dashboard/frontend/util/product/product.mapper';
import { ProductCategory, ProductDriveType } from '@phoenix/team-m/dashboard/frontend/util/product/product.model';

import { PRODUCT_PAGE_SIZE } from './recommended-products';

export interface RecommendedProduct extends NextBestOffer {
  imageUrl: string;
  driveType?: ProductDriveType;
  category?: ProductCategory;
}

export const mapNextBestOffers = (products: NextBestOffer[]): RecommendedProduct[] =>
  products.map((product) => ({
    ...product,
    imageUrl: product.imageUrl || '',
    driveType: mapProductDriveType(product.driveType),
    category: mapProductCategory(product.category),
  }));

export const useRecommendedProducts = (): { products: RecommendedProduct[]; hasEnoughRecommendations: boolean } => {
  const {
    auth: { user },
  } = useCore();

  const stihlId = user?.stihl_id || '';
  const { 'next-best-offers': flagEnabled } = useFlags();

  const [{ data }] = useGetCustomerRecommendationsQuery({
    pause: !stihlId || !flagEnabled,
    context: useQueryContextMemo(process.env.NEXT_PUBLIC_TOOL_SHED_GRAPHQL_BASE_URL),
    variables: {
      input: {
        origin: NextBestOfferOrigin.MystihlWebDashboard,
      },
    },
  });

  const recommendations = data?.productRecommendations?.nextBestOffers?.customerRecommendations || [];

  return {
    products: mapNextBestOffers(recommendations.filter((product): product is NextBestOffer => product !== null)),
    hasEnoughRecommendations: recommendations?.length >= PRODUCT_PAGE_SIZE,
  };
};
